/**
 * Thin client wrappers over @aurora/core's pure date helpers.
 *
 * Only this module is allowed to read the wall clock. Everything else takes
 * `now` as an argument, which keeps logic testable and makes "show me last
 * Tuesday" a parameter change rather than a rewrite.
 */

import { ageInMonths } from '@aurora/core'

const DEFAULT_TZ =
  typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC'

export function tz(preferred?: string): string {
  return preferred || DEFAULT_TZ
}

/** Today as an ISO calendar date in the given timezone. */
export function todayISO(preferred?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz(preferred),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

export function nowMs(): number {
  return Date.now()
}

export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

/** "Tuesday, June 2" — the header format. */
export function formatFriendlyDate(iso: string, preferred?: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1, 12))
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz(preferred),
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(dt)
}

/** Today / Tomorrow / Yesterday / the weekday name. */
export function relativeDay(date: string, today: string): string {
  if (date === today) return 'Today'
  const d = new Date(`${date}T12:00:00Z`).getTime()
  const t = new Date(`${today}T12:00:00Z`).getTime()
  const diff = Math.round((d - t) / 86_400_000)
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
}

/** "3:12 PM" in the child's day. */
export function formatTime(ms: number, preferred?: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz(preferred), hour: 'numeric', minute: '2-digit' }).format(new Date(ms))
}

/** Relative phrasing that stays warm rather than clinical. */
export function formatRelative(ts: number, now: number = Date.now()): string {
  const diff = now - ts
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const days = Math.floor(hr / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return weeks === 1 ? 'last week' : `${weeks} weeks ago`
  const months = Math.floor(days / 30)
  return months === 1 ? 'last month' : `${months} months ago`
}

/** "2y 9m". Guidance for the parent's own sense of scale, never a judgement. */
export function formatAge(birthDate?: string, preferred?: string): string | undefined {
  if (!birthDate) return undefined
  try {
    return ageInMonths(birthDate as any, Date.now(), tz(preferred)).label
  } catch {
    return undefined
  }
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h}h ${m}m` : `${h}h`
}
